import {
  Banner,
  BannerAction,
  BannerClose,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { isTauri } from '@heidi/runtime/platform';
import { cn } from '@heidi/utils';
import { Download, Laptop } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type {
  TrackWorkDesktopNurtureBannerEvent,
  TrackWorkUpgradeActioned,
  WorkDesktopNurtureBannerEventName,
} from '../../../contracts/work-launch-analytics.types';
import { DESKTOP_APP_DOWNLOAD_URL } from '../../../data/workflows/lib/preset-meta';

const ACCOUNT_MATURITY_DAYS = 7;

const NURTURE_ANALYTICS = {
  viewed: 'work_desktop_nurture_banner_viewed',
  ctaClicked: 'work_desktop_nurture_banner_cta_clicked',
  dismissed: 'work_desktop_nurture_banner_dismissed',
} as const satisfies Record<string, WorkDesktopNurtureBannerEventName>;

export interface RoutinesDesktopNurtureBannerProps {
  className?: string;
}

/**
 * What the app supplies: the kill switch, the persisted dismissal (an app atom,
 * as a value and setter), the routine-run and account reads, and both emits.
 */
export interface RoutinesDesktopNurtureBannerHost {
  isEnabled: boolean;
  dismissed: boolean;
  setDismissed: (dismissed: boolean) => void;
  /** Routine runs so far, and how many earn the banner (inclusive). */
  runCount: number;
  minimumRunCount: number;
  isManaged: boolean;
  /** Account creation time; the banner waits until the account is a week old. */
  accountCreatedAt?: string | null;
  /** Module-level, so the impression effect never re-runs on their identity. */
  trackNurtureEvent: TrackWorkDesktopNurtureBannerEvent;
  trackUpgradeActioned: TrackWorkUpgradeActioned;
}

/**
 * Soft upsell after a clinician has run enough routines on web.
 * Points them at the desktop app to unlock computer-use / screen automation.
 * Dismissible; hidden on Tauri and for managed accounts.
 */
export const RoutinesDesktopNurtureBanner = ({
  className,
  isEnabled,
  dismissed,
  setDismissed,
  runCount: liveCount,
  minimumRunCount,
  isManaged,
  accountCreatedAt,
  trackNurtureEvent,
  trackUpgradeActioned,
}: RoutinesDesktopNurtureBannerProps & RoutinesDesktopNurtureBannerHost) => {
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  const hasTrackedImpressionRef = useRef(false);
  useEffect(() => setMounted(true), []);

  const isUserAtLeastAWeekOld = accountCreatedAt
    ? Date.now() - new Date(accountCreatedAt).getTime() >=
      ACCOUNT_MATURITY_DAYS * 24 * 60 * 60 * 1000
    : false;
  const meetsThreshold = liveCount >= minimumRunCount;

  // Mount gate avoids SSR/client mismatch on ``isTauri()`` (false on server).
  const visible =
    mounted &&
    isEnabled &&
    !isTauri() &&
    !isManaged &&
    !dismissed &&
    meetsThreshold &&
    isUserAtLeastAWeekOld;

  useEffect(() => {
    if (visible && !hasTrackedImpressionRef.current) {
      hasTrackedImpressionRef.current = true;
      trackNurtureEvent(NURTURE_ANALYTICS.viewed, { routine_run_count: liveCount });
      trackUpgradeActioned({
        action: 'viewed',
        entry_point: 'desktop_required',
        surface: 'work_home',
      });
    }
  }, [visible, liveCount, trackNurtureEvent, trackUpgradeActioned]);

  const handleCtaClick = useCallback(() => {
    trackNurtureEvent(NURTURE_ANALYTICS.ctaClicked, { routine_run_count: liveCount });
    trackUpgradeActioned({
      action: 'cta_clicked',
      entry_point: 'desktop_required',
      surface: 'work_home',
    });
  }, [liveCount, trackNurtureEvent, trackUpgradeActioned]);

  const handleDismiss = useCallback(() => {
    trackNurtureEvent(NURTURE_ANALYTICS.dismissed, { routine_run_count: liveCount });
    trackUpgradeActioned({
      action: 'dismissed',
      entry_point: 'desktop_required',
      surface: 'work_home',
    });
    setDismissed(true);
  }, [liveCount, setDismissed, trackNurtureEvent, trackUpgradeActioned]);

  return (
    <AnimatePresence>
      {visible && (
        <m.div
          initial={{ opacity: 0, y: 8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: 8, height: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="overflow-hidden"
          data-testid="routines-desktop-nurture-banner"
        >
          <Banner className={cn('my-1', className)}>
            <BannerContent icon={<Laptop />}>
              <BannerTitle data-testid="routines-desktop-nurture-banner-title">
                <FormattedMessage
                  defaultMessage="You've run {count, plural, one {# routine} other {# routines}}"
                  id="ZXkfdvz6eX"
                  description="Title of the Work desktop nurture banner after enough routine runs on web"
                  values={{ count: liveCount }}
                />
              </BannerTitle>
              <BannerDescription>
                <FormattedMessage
                  defaultMessage="Get the desktop app to unlock screen automation and working the computer in Heidi."
                  id="7viGpja9eU"
                  description="Subtitle of the Work desktop nurture banner encouraging download"
                />
              </BannerDescription>
            </BannerContent>
            <BannerAction>
              <Button
                type="button"
                size="xs"
                asChild
                data-testid="routines-desktop-nurture-banner-cta"
              >
                <a
                  href={DESKTOP_APP_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleCtaClick}
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Get the desktop app',
                    id: 'FwixZwE/Xd',
                    description:
                      'Aria label for the desktop download CTA on the Work routines nurture banner',
                  })}
                >
                  <Download className="size-3.5" aria-hidden />
                  <FormattedMessage
                    defaultMessage="Get the desktop app"
                    id="i/iSsIm4uF"
                    description="CTA on the Work routines nurture banner linking to desktop download"
                  />
                </a>
              </Button>
            </BannerAction>
            <BannerClose
              onClick={handleDismiss}
              data-testid="routines-desktop-nurture-banner-close"
            />
          </Banner>
        </m.div>
      )}
    </AnimatePresence>
  );
};
